import React, { useState, useEffect, useRef } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { Helmet } from "react-helmet"
import NavigationBar from "../../components/ui/NavigationBar"
import Icon from "../../components/AppIcon"
import Button from "../../components/ui/Button"
import { authHelpers } from "../../firebaseClient"
import {
  getLiveClass,
  startLiveClass,
  endLiveClass,
  subscribeAttendees,
  subscribeChatMessages,
  sendChatMessage,
  subscribeReactions,
  kickAttendee,
  createPoll,
  closePoll,
  subscribeActivePoll,
  subscribePollVotes,
    createLiveQuiz,
  closeLiveQuiz,
  subscribeActiveLiveQuiz,
  subscribeQuizAnswers,
  setWhiteboardOpen,
} from "../../utils/liveClassHelpers"
import { createAgoraClient, joinChannel, createLocalTracks, leaveChannel } from "../../utils/agoraHelpers"
import LiveWhiteboard from "../../components/LiveWhiteboard"

const LiveClassroomHost = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const orgId = searchParams?.get("orgId")
  const classId = searchParams?.get("classId")

  const [currentUser, setCurrentUser] = useState(null)
  const [liveClass, setLiveClass] = useState(null)
  const [loading, setLoading] = useState(true)
  const [micOn, setMicOn] = useState(true)
  const [cameraOn, setCameraOn] = useState(true)
  const [attendees, setAttendees] = useState([])
  const [chatMessages, setChatMessages] = useState([])
  const [reactions, setReactions] = useState([])
  const [chatInput, setChatInput] = useState("")
  const [showAttendees, setShowAttendees] = useState(false)
  const [activePoll, setActivePoll] = useState(null)
  const [pollVotes, setPollVotes] = useState([])
  const [showPollModal, setShowPollModal] = useState(false)
  const [pollQuestion, setPollQuestion] = useState("")
  const [pollOptions, setPollOptions] = useState(["", ""])
  const [activeQuiz, setActiveQuiz] = useState(null)
  const [quizAnswers, setQuizAnswers] = useState([])
  const [showQuizModal, setShowQuizModal] = useState(false)
  const [quizQuestion, setQuizQuestion] = useState("")
  const [quizOptions, setQuizOptions] = useState(["", ""])
  const [quizCorrectIndex, setQuizCorrectIndex] = useState(0)
  const [whiteboardOpen, setWhiteboardOpenState] = useState(false)
  const [agoraReady, setAgoraReady] = useState(false)
  const clientRef = useRef(null)
  const localTracksRef = useRef(null)
  const videoContainerRef = useRef(null)

  // ── load the class + verify this user is the instructor ──
  useEffect(() => {
    const load = async () => {
      const { session } = await authHelpers.getSession()
      if (!session) {
        navigate("/authentication-portal", { replace: true })
        return
      }
      setCurrentUser(session.user)

      const { liveClass: data } = await getLiveClass(orgId, classId)
      if (!data || data?.instructorUid !== session.user.uid) {
        navigate("/organization-dashboard", { replace: true })
        return
      }
      setLiveClass(data)
      setLoading(false)
    }
    load()
  }, [orgId, classId, navigate])

  // ── go live: mark status + join Agora as broadcaster ──
  useEffect(() => {
    if (!currentUser || !classId) return
    let cancelled = false

    const goLive = async () => {
      await startLiveClass(orgId, classId)

      const client = createAgoraClient()
      clientRef.current = client
      await joinChannel(client, classId, currentUser.uid, "broadcaster")

      const { audioTrack, videoTrack } = await createLocalTracks()
      localTracksRef.current = { audioTrack, videoTrack }
      if (cancelled) return

            videoTrack.play(videoContainerRef.current)
      await client.publish([audioTrack, videoTrack])
      if (!cancelled) setAgoraReady(true)
    }

    goLive().catch((e) => console.error("[LiveClassroomHost] failed to go live:", e))

    return () => {
      cancelled = true
      leaveChannel(clientRef.current, localTracksRef.current).catch(() => {})
    }
  }, [currentUser, classId, orgId])

        // ── subscribe attendees / chat / reactions / active poll / active quiz ──
  useEffect(() => {
    if (!orgId || !classId) return
    const u1 = subscribeAttendees(orgId, classId, setAttendees)
    const u2 = subscribeChatMessages(orgId, classId, setChatMessages)
    const u3 = subscribeReactions(orgId, classId, setReactions)
    const u4 = subscribeActivePoll(orgId, classId, setActivePoll)
    const u5 = subscribeActiveLiveQuiz(orgId, classId, setActiveQuiz)
    return () => { u1(); u2(); u3(); u4(); u5(); }
  }, [orgId, classId])

    // ── live vote tally for whichever poll is currently active ──
  useEffect(() => {
    if (!orgId || !classId || !activePoll?.id) { setPollVotes([]); return }
    return subscribePollVotes(orgId, classId, activePoll.id, setPollVotes)
  }, [orgId, classId, activePoll?.id])

  // ── live answer tally for whichever quiz question is currently active ──
  useEffect(() => {
    if (!orgId || !classId || !activeQuiz?.id) { setQuizAnswers([]); return }
    return subscribeQuizAnswers(orgId, classId, activeQuiz.id, setQuizAnswers)
  }, [orgId, classId, activeQuiz?.id])

    // Kept records (leftAtMs set, not deleted) support future attendance
  // history — filter to who's actually still here for anything shown live.
  const activeAttendees = attendees?.filter((a) => !a?.leftAtMs) || []

  const toggleMic = async () => {
    await localTracksRef.current?.audioTrack?.setEnabled(!micOn)
    setMicOn((m) => !m)
  }

  const toggleCamera = async () => {
    await localTracksRef.current?.videoTrack?.setEnabled(!cameraOn)
    setCameraOn((c) => !c)
  }

    const handleSendChat = async () => {
    if (!chatInput?.trim()) return
    await sendChatMessage(orgId, classId, currentUser.uid, currentUser.displayName || "Instructor", currentUser.photoURL, chatInput)
    setChatInput("")
  }

  const handlePollOptionChange = (index) => (e) => {
    setPollOptions((prev) => prev.map((opt, i) => (i === index ? e?.target?.value : opt)))
  }

  const handleAddPollOption = () => setPollOptions((prev) => [...prev, ""])

  const handleLaunchPoll = async () => {
    const cleanOptions = pollOptions.map((o) => o?.trim()).filter(Boolean)
    if (!pollQuestion?.trim() || cleanOptions.length < 2) return
    await createPoll(orgId, classId, { question: pollQuestion.trim(), options: cleanOptions })
    setPollQuestion("")
    setPollOptions(["", ""])
    setShowPollModal(false)
  }

    const handleClosePoll = async () => {
    if (!activePoll?.id) return
    await closePoll(orgId, classId, activePoll.id)
  }

  const pollTally = (optionId) => pollVotes?.filter((v) => v?.optionId === optionId)?.length || 0

  const handleQuizOptionChange = (index) => (e) => {
    setQuizOptions((prev) => prev.map((opt, i) => (i === index ? e?.target?.value : opt)))
  }

  const handleAddQuizOption = () => setQuizOptions((prev) => [...prev, ""])

  const handleLaunchQuiz = async () => {
    const cleanOptions = quizOptions.map((o) => o?.trim()).filter(Boolean)
    if (!quizQuestion?.trim() || cleanOptions.length < 2) return
    const correctOptionId = `opt${quizCorrectIndex}`
    await createLiveQuiz(orgId, classId, { question: quizQuestion.trim(), options: cleanOptions, correctOptionId })
    setQuizQuestion("")
    setQuizOptions(["", ""])
    setQuizCorrectIndex(0)
    setShowQuizModal(false)
  }

  const handleCloseQuiz = async () => {
    if (!activeQuiz?.id) return
    await closeLiveQuiz(orgId, classId, activeQuiz.id)
  }

    const quizTally = (optionId) => quizAnswers?.filter((a) => a?.optionId === optionId)?.length || 0
  const quizCorrectCount = quizAnswers?.filter((a) => a?.correct)?.length || 0

  const handleToggleWhiteboard = async () => {
    const next = !whiteboardOpen
    setWhiteboardOpenState(next)
    await setWhiteboardOpen(orgId, classId, next)
  }

  const handleEndClass = async () => {
    if (!window.confirm("End this live class for everyone?")) return
    await leaveChannel(clientRef.current, localTracksRef.current)
    await endLiveClass(orgId, classId)
    navigate(`/organization-course-builder?orgId=${orgId}&courseId=${liveClass?.courseId}`)
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex items-center space-x-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <span className="text-muted-foreground">Setting up your classroom...</span>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Helmet><title>{liveClass?.title} — Live — Nesolearn</title></Helmet>
      <NavigationBar />

        <div className="flex-1 flex flex-col lg:flex-row max-w-7xl mx-auto w-full px-4 pb-4 pt-24 sm:pt-28 gap-4">
        <div className="flex-1 flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="flex items-center gap-1.5 bg-destructive text-white text-xs font-bold px-2 py-1 rounded-full">
                <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> LIVE
              </span>
              <h1 className="font-semibold text-foreground">{liveClass?.title}</h1>
            </div>
                      <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" iconName="BarChart3" iconPosition="left" onClick={() => setShowPollModal(true)}>
                Poll
              </Button>
                    <Button variant="outline" size="sm" iconName="HelpCircle" iconPosition="left" onClick={() => setShowQuizModal(true)}>
                Quiz
              </Button>
              <Button variant={whiteboardOpen ? "default" : "outline"} size="sm" iconName="PenLine" iconPosition="left" onClick={handleToggleWhiteboard}>
                Whiteboard
              </Button>
                            <Button variant="ghost" size="sm" iconName="Users" iconPosition="left" onClick={() => setShowAttendees((s) => !s)}>
                {activeAttendees?.length || 0}
              </Button>
            </div>
          </div>

          {activePoll && (
            <div className="bg-card border border-border rounded-lg p-3 space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-foreground">{activePoll?.question}</p>
                {activePoll?.status === "open" && (
                  <Button variant="ghost" size="sm" onClick={handleClosePoll}>Close</Button>
                )}
              </div>
              {activePoll?.options?.map((opt) => {
                const count = pollTally(opt?.id)
                const total = pollVotes?.length || 0
                const pct = total > 0 ? Math.round((count / total) * 100) : 0
                return (
                  <div key={opt?.id} className="space-y-0.5">
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>{opt?.text}</span>
                      <span>{count} ({pct}%)</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                      <div className="h-full bg-accent" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                )
              })}
            </div>
          )}

                              <div ref={videoContainerRef} className="bg-black rounded-xl aspect-video w-full relative overflow-hidden" />

          {activeQuiz && (
            <div className="bg-card border border-border rounded-lg p-3 space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-foreground">{activeQuiz?.question}</p>
                {activeQuiz?.status === "open" ? (
                  <Button variant="ghost" size="sm" onClick={handleCloseQuiz}>Reveal Answer</Button>
                ) : (
                  <span className="text-xs text-muted-foreground">{quizCorrectCount}/{quizAnswers?.length || 0} correct</span>
                )}
              </div>
              {activeQuiz?.options?.map((opt) => {
                const count = quizTally(opt?.id)
                const total = quizAnswers?.length || 0
                const pct = total > 0 ? Math.round((count / total) * 100) : 0
                const isCorrect = opt?.id === activeQuiz?.correctOptionId
                const revealed = activeQuiz?.status === "closed"
                return (
                  <div key={opt?.id} className="space-y-0.5">
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span className={revealed && isCorrect ? "text-success font-medium" : ""}>
                        {opt?.text}{revealed && isCorrect ? " ✓" : ""}
                      </span>
                      <span>{count} ({pct}%)</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                      <div className={`h-full ${revealed && isCorrect ? "bg-success" : "bg-accent"}`} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                )
              })}
            </div>
          )}

                    {whiteboardOpen && (
                        <LiveWhiteboard client={agoraReady ? clientRef.current : null} isHost attendeeCount={activeAttendees?.length} />
          )}

          <div className="flex items-center justify-center gap-3">
            <Button variant={micOn ? "outline" : "destructive"} size="icon" iconName={micOn ? "Mic" : "MicOff"} onClick={toggleMic} />
            <Button variant={cameraOn ? "outline" : "destructive"} size="icon" iconName={cameraOn ? "Video" : "VideoOff"} onClick={toggleCamera} />
            <Button variant="destructive" iconName="PhoneOff" iconPosition="left" onClick={handleEndClass}>
              End Class
            </Button>
          </div>
        </div>

        <div className="w-full lg:w-80 flex flex-col bg-card border border-border rounded-xl overflow-hidden">
          {showAttendees ? (
            <div className="flex-1 overflow-y-auto p-3 space-y-2">
                            <h3 className="text-sm font-semibold text-foreground px-1">Attendees ({activeAttendees?.length || 0})</h3>
              {activeAttendees?.map((a) => (
                <div key={a?.uid} className="flex items-center justify-between px-2 py-1.5 rounded-lg hover:bg-muted">
                  <span className="text-sm text-foreground">{a?.name}</span>
                  <Button variant="ghost" size="icon" iconName="X" iconSize={14} onClick={() => kickAttendee(orgId, classId, a?.uid)} />
                </div>
              ))}
            </div>
          ) : (
            <>
              <div className="flex-1 overflow-y-auto p-3 space-y-2">
                {chatMessages?.map((m) => (
                  <div key={m?.id} className="text-sm">
                    <span className="font-medium text-foreground">{m?.name}: </span>
                    <span className="text-muted-foreground">{m?.text}</span>
                  </div>
                ))}
              </div>
              <div className="p-3 border-t border-border flex gap-2">
                <input
                  value={chatInput}
                  onChange={(e) => setChatInput(e?.target?.value)}
                  onKeyDown={(e) => e?.key === "Enter" && handleSendChat()}
                  placeholder="Message..."
                  className="flex-1 rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                />
                <Button variant="default" size="icon" iconName="Send" iconSize={14} onClick={handleSendChat} />
              </div>
            </>
          )}
                </div>
      </div>

            {showPollModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-xl p-5 w-full max-w-md space-y-4">
            <h3 className="font-semibold text-foreground">Launch a Poll</h3>
            <input
              value={pollQuestion}
              onChange={(e) => setPollQuestion(e?.target?.value)}
              placeholder="Ask a question..."
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
            <div className="space-y-2">
              {pollOptions.map((opt, i) => (
                <input
                  key={i}
                  value={opt}
                  onChange={handlePollOptionChange(i)}
                  placeholder={`Option ${i + 1}`}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                />
              ))}
              <Button variant="ghost" size="sm" iconName="Plus" iconPosition="left" onClick={handleAddPollOption}>
                Add option
              </Button>
            </div>
            <div className="flex items-center justify-end gap-2">
              <Button variant="ghost" onClick={() => setShowPollModal(false)}>Cancel</Button>
              <Button variant="default" onClick={handleLaunchPoll}>Launch Poll</Button>
            </div>
          </div>
        </div>
      )}

      {showQuizModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-xl p-5 w-full max-w-md space-y-4">
            <h3 className="font-semibold text-foreground">Launch a Quiz Question</h3>
            <input
              value={quizQuestion}
              onChange={(e) => setQuizQuestion(e?.target?.value)}
              placeholder="Ask a question..."
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">Select the correct answer:</p>
              {quizOptions.map((opt, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="quizCorrectOption"
                    checked={quizCorrectIndex === i}
                    onChange={() => setQuizCorrectIndex(i)}
                  />
                  <input
                    value={opt}
                    onChange={handleQuizOptionChange(i)}
                    placeholder={`Option ${i + 1}`}
                    className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
              ))}
              <Button variant="ghost" size="sm" iconName="Plus" iconPosition="left" onClick={handleAddQuizOption}>
                Add option
              </Button>
            </div>
            <div className="flex items-center justify-end gap-2">
              <Button variant="ghost" onClick={() => setShowQuizModal(false)}>Cancel</Button>
              <Button variant="default" onClick={handleLaunchQuiz}>Launch Quiz</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default LiveClassroomHost